/* ============================================================
   SUSUN MENU — kawalan susunan senarai v3.9
   ============================================================
   Satu butang, satu menu. Jenis susunan disenaraikan sekali
   sahaja; ARAH ditukar dengan menekan jenis yang SAMA sekali
   lagi (keputusan user 2026-08-10). Workspace lama menyenaraikan
   ENAM baris berasingan — naik dan turun bagi setiap jenis —
   jadi menunya dua kali panjang untuk bilangan pilihan sebenar
   yang sama.

   ⭐ DIKONGSI, bukan disalin. Ia dipakai oleh senarai produk
      /workspace-v3.9 DAN modal Add Product. Dua salinan kawalan
      yang sama bermakna satu daripadanya akan ketinggalan senyap
      apabila peraturan susunan berubah — dan pengguna akan
      berjumpa dua menu yang nampak serupa tetapi berkelakuan
      berlainan.

   Senarai jenis datang dari LUAR (`pilihan`) kerana setiap
   senarai ada medan sendiri: kad workspace boleh disusun ikut
   Module, produk library tidak. Bentuk setiap item:

     { id, label, lalai: 'asc'|'desc', tip: { asc, desc } }

   `lalai` ialah arah yang paling kerap dikehendaki bagi jenis
   itu, dipakai apabila pengguna BERTUKAR jenis. Tanpa ia,
   menukar ke "Time" akan memberi yang paling lama di atas —
   hampir tidak pernah itu yang dicari.
   ============================================================ */

import { useEffect, useRef, useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Check } from 'lucide-react';

import { Btn } from '../design/ui';

export default function SusunMenuV39({ pilihan, jenis, arah, onTukar }) {
    const [buka, setBuka] = useState(false);
    const rujuk = useRef(null);

    /* Tutup bila diklik di luar atau Esc — corak sama seperti
       GroupSwitcherV39. Tanpa ini menu tersangkut terbuka dan menutup
       baris produk di bawahnya. */
    useEffect(() => {
        if (!buka) return undefined;
        const luar = (e) => { if (rujuk.current && !rujuk.current.contains(e.target)) setBuka(false); };
        const esc = (e) => { if (e.key === 'Escape') setBuka(false); };
        document.addEventListener('mousedown', luar);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', luar);
            document.removeEventListener('keydown', esc);
        };
    }, [buka]);

    const kini = pilihan.find((s) => s.id === jenis) || pilihan[0];
    const IkonArah = arah === 'asc' ? ArrowUp : ArrowDown;

    return (
        <div ref={rujuk} style={{ position: 'relative' }}>
            <Btn
                variant="ghost"
                size="sm"
                onClick={() => setBuka((v) => !v)}
                icon={<ArrowUpDown size={14} strokeWidth={2} />}
                aria-haspopup="menu"
                aria-expanded={buka}
            >
                {kini.label}
                <IkonArah size={12} strokeWidth={2.4} style={{ marginLeft: 3, color: 'var(--s-ink-3)' }} />
            </Btn>

            {/* Menu dijajar ke KANAN, bukan kiri seperti lalai `.s-pop`. Butang
                ini duduk di hujung kanan bar, jadi menu yang bermula dari tepi
                kirinya melimpah keluar lajur dan teks arahnya terpotong — itu
                yang berlaku pada percubaan pertama. */}
            {buka && (
                <div className="s-pop" role="menu" style={{ minWidth: 210, left: 'auto', right: 0 }}>
                    {pilihan.map((s) => {
                        const aktif = s.id === jenis;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                role="menuitem"
                                className="s-pop__row"
                                onClick={() => {
                                    /* Jenis sama → balikkan arah. Jenis lain → guna
                                       arah lalai jenis itu, bukan arah semasa. */
                                    onTukar(s.id, aktif ? (arah === 'asc' ? 'desc' : 'asc') : s.lalai);
                                }}
                            >
                                <span style={{
                                    flex: 1, fontSize: 13,
                                    color: aktif ? 'var(--s-ink)' : 'var(--s-ink-2)',
                                }}>
                                    {s.label}
                                </span>
                                <span className="s-dim" style={{ fontSize: 11.5 }}>
                                    {s.tip[aktif ? arah : s.lalai]}
                                </span>
                                {aktif && <Check size={13} strokeWidth={2.2} style={{ color: 'var(--s-ink)', flex: 'none' }} />}
                            </button>
                        );
                    })}
                    <span className="s-pop__sep" />
                    <p style={{
                        margin: 0, padding: '2px 9px 5px',
                        fontSize: 11.5, lineHeight: 1.45, color: 'var(--s-ink-3)',
                    }}>
                        Press the same option again to reverse the direction.
                    </p>
                </div>
            )}
        </div>
    );
}
